import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { _createThread } from '@/api/chat/mutations';
import { CHAT_QUERY_KEYS, chatQueryKey } from '@/api/chat/query-keys';
import {
  type CreateThreadType,
  type ParticipantType,
  RECIPIENT_KIND_BY_PARTICIPANT_TYPE,
  type ThreadAbout,
} from '@/api/chat/types';
import { useChatIdentityKey } from '@/api/chat/use-chat-identity-key';
import { useSelectedUserConfigSlice } from '@/stores/selectors';
import { emitToast } from '@/utils/emit-toast';

import { seedContextThread } from '../data/context-thread-cache';
import { conversationStartError, deriveSendAsIdentity, threadFromCreated } from '../utils';
import {
  contactContextId,
  getCommunicationContactPath,
} from '../utils/get-communication-contact-path';

export const THREAD_NAME_MAX_LENGTH = 100;

export type TypedThreadType = Extract<CreateThreadType, 'Inquiry' | 'Report' | 'Support'>;

export type StartTypedThreadInput = {
  recipientType: ParticipantType;
  recipientId: string;
  name: string;
} & (
  | { type: Extract<TypedThreadType, 'Inquiry'>; about?: ThreadAbout }
  | { type: Exclude<TypedThreadType, 'Inquiry'>; about?: never }
);

export const START_TYPED_THREAD_FAILURE_TITLE = "Couldn't start the conversation";
export const START_TYPED_THREAD_FAILURE_MESSAGE = 'Please try again.';

export const useStartTypedThread = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const identity = useChatIdentityKey();
  const { selectedUserRole, selectedEntityId } = useSelectedUserConfigSlice();
  const sendingAsKind = deriveSendAsIdentity({
    role: selectedUserRole,
    entityId: selectedEntityId,
  }).kind;
  const inFlight = useRef(false);

  const { mutate, isPending } = useMutation({
    mutationFn: ({ recipientType, recipientId, name, ...typeAndAbout }: StartTypedThreadInput) =>
      _createThread({
        name: name.slice(0, THREAD_NAME_MAX_LENGTH),
        recipients: [{ id: recipientId, type: RECIPIENT_KIND_BY_PARTICIPANT_TYPE[recipientType] }],
        ...typeAndAbout,
      }),
    onSuccess: (thread, { recipientType, recipientId }) => {
      seedContextThread(
        queryClient,
        identity,
        contactContextId(recipientType, recipientId),
        threadFromCreated(thread, sendingAsKind),
      );
      void queryClient.invalidateQueries({
        queryKey: chatQueryKey(identity, CHAT_QUERY_KEYS.contexts),
      });
      void queryClient.invalidateQueries({
        queryKey: chatQueryKey(identity, CHAT_QUERY_KEYS.contextThreads),
      });
      navigate(getCommunicationContactPath({ recipientType, recipientId, threadId: thread.id }));
    },
    onError: (error) => {
      emitToast({
        type: 'error',
        title: START_TYPED_THREAD_FAILURE_TITLE,
        message: conversationStartError(error, START_TYPED_THREAD_FAILURE_MESSAGE),
      });
    },
    onSettled: () => {
      inFlight.current = false;
    },
  });

  // isPending re-renders too late to stop a fast double click from creating two threads.
  const start = useCallback(
    (input: StartTypedThreadInput) => {
      if (inFlight.current) return;
      inFlight.current = true;
      mutate(input);
    },
    [mutate],
  );

  return { start, isPending };
};
